/**
 * Create Rubric Page
 *
 * Single-page form with repeatable sections for dimensions and rules.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';

import { RequestPanel } from '../../components/RequestPanel';
import {
  validateRubricForm,
  generateFormId,
  type RubricFormData,
  type DimensionFormData,
  type RuleFormData,
} from '../../lib/trainer-validation';
import type { ApiResult, Rubric } from '../../lib/types';

const emptyRule = (): RuleFormData => ({
  id: generateFormId(),
  type: 'keyword',
  pattern: '',
  score: 1,
  required: false,
  caseSensitive: false,
});

const emptyDimension = (): DimensionFormData => ({
  id: generateFormId(),
  name: '',
  description: '',
  weight: 1,
  rules: [emptyRule()],
});

const initialFormData: RubricFormData = {
  name: '',
  description: '',
  passingScore: 70,
  dimensions: [emptyDimension()],
};

export function CreateRubric() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RubricFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<Rubric> | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);

  const validate = (): boolean => {
    const errs = validateRubricForm(formData);
    setErrors(errs);
    return errs.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setResult(null);
    setApiError(null);

    const res = await apiClient.createRubric({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      passingScore: formData.passingScore,
      dimensions: formData.dimensions.map((dim) => ({
        id: dim.id,
        name: dim.name.trim(),
        description: dim.description.trim() || undefined,
        weight: dim.weight,
        rules: dim.rules.map((rule) => ({
          id: rule.id,
          type: rule.type,
          pattern: rule.pattern.trim(),
          score: rule.score,
          required: rule.required,
          caseSensitive: rule.caseSensitive,
        })),
      })),
    });

    setResult(res);
    setLoading(false);

    if (res.response.data) {
      navigate(`/console/trainer/rubrics/${res.response.data.id}`);
    } else if (res.response.error) {
      const err = res.response.error;
      let errorMsg = `Failed to create rubric: ${err.message}`;
      if (err.details) {
        const detailMsgs = Object.entries(err.details)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join(', ');
        errorMsg += ` (${detailMsgs})`;
      }
      setApiError(errorMsg);
    }
  };

  const updateDimension = (index: number, updates: Partial<DimensionFormData>) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: prev.dimensions.map((d, i) => (i === index ? { ...d, ...updates } : d)),
    }));
  };

  const addDimension = () => {
    setFormData((prev) => ({
      ...prev,
      dimensions: [...prev.dimensions, emptyDimension()],
    }));
  };

  const removeDimension = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: prev.dimensions.filter((_, i) => i !== index),
    }));
  };

  const updateRule = (dimIndex: number, ruleIndex: number, updates: Partial<RuleFormData>) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: prev.dimensions.map((d, i) =>
        i === dimIndex
          ? {
              ...d,
              rules: d.rules.map((r, j) => (j === ruleIndex ? { ...r, ...updates } : r)),
            }
          : d
      ),
    }));
  };

  const addRule = (dimIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: prev.dimensions.map((d, i) =>
        i === dimIndex ? { ...d, rules: [...d.rules, emptyRule()] } : d
      ),
    }));
  };

  const removeRule = (dimIndex: number, ruleIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: prev.dimensions.map((d, i) =>
        i === dimIndex ? { ...d, rules: d.rules.filter((_, j) => j !== ruleIndex) } : d
      ),
    }));
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Link to="/console/trainer/rubrics" style={{ color: 'var(--text-tertiary)' }}>
          &larr; Back to Rubrics
        </Link>
      </div>

      <h1>Create Rubric</h1>

      {apiError && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          {apiError}
        </div>
      )}

      {errors.length > 0 && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Info */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Basic Information</h2>
          <div className="form-group">
            <label className="label">Name *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g., Customer Service Call Rubric"
            />
          </div>
          <div className="form-group">
            <label className="label">Description</label>
            <textarea
              className="input"
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              placeholder="Optional description of this rubric"
              rows={2}
            />
          </div>
          <div className="form-group" style={{ maxWidth: 200 }}>
            <label className="label">Passing Score (0-100) *</label>
            <input
              type="number"
              className="input"
              value={formData.passingScore}
              onChange={(e) => setFormData((p) => ({ ...p, passingScore: Number(e.target.value) }))}
              min={0}
              max={100}
            />
          </div>
        </div>

        {/* Dimensions */}
        {formData.dimensions.map((dim, dimIndex) => (
          <div key={dimIndex} className="card" style={{ marginBottom: 16 }}>
            <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
              <h2>Dimension {dimIndex + 1}</h2>
              {formData.dimensions.length > 1 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => removeDimension(dimIndex)}
                  style={{ fontSize: 12, padding: '4px 12px', color: 'var(--semantic-error)' }}
                >
                  Remove Dimension
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">Name *</label>
                <input
                  type="text"
                  className="input"
                  value={dim.name}
                  onChange={(e) => updateDimension(dimIndex, { name: e.target.value })}
                  placeholder="e.g., Greeting"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">Description</label>
                <input
                  type="text"
                  className="input"
                  value={dim.description}
                  onChange={(e) => updateDimension(dimIndex, { description: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
                <label className="label">Weight *</label>
                <input
                  type="number"
                  className="input"
                  value={dim.weight}
                  onChange={(e) => updateDimension(dimIndex, { weight: Number(e.target.value) })}
                  min={0.1}
                  step={0.1}
                />
              </div>
            </div>

            {/* Rules */}
            <div style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 12 }}>Rules</h3>
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Type</th>
                    <th>Pattern</th>
                    <th style={{ width: 80 }}>Score</th>
                    <th style={{ width: 80 }}>Required</th>
                    <th style={{ width: 80 }}>Case</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {dim.rules.map((rule, ruleIndex) => (
                    <tr key={ruleIndex}>
                      <td>
                        <select
                          className="input"
                          value={rule.type}
                          onChange={(e) =>
                            updateRule(dimIndex, ruleIndex, {
                              type: e.target.value as 'keyword' | 'regex',
                            })
                          }
                          style={{ padding: '4px 8px', fontSize: 13 }}
                        >
                          <option value="keyword">keyword</option>
                          <option value="regex">regex</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="input mono"
                          value={rule.pattern}
                          onChange={(e) => updateRule(dimIndex, ruleIndex, { pattern: e.target.value })}
                          placeholder={rule.type === 'keyword' ? 'e.g., "thank you", "I understand"' : 'e.g., (help|assist|resolve)'}
                          style={{ padding: '4px 8px', fontSize: 13 }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input"
                          value={rule.score}
                          onChange={(e) =>
                            updateRule(dimIndex, ruleIndex, { score: Number(e.target.value) })
                          }
                          style={{ padding: '4px 8px', fontSize: 13 }}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={rule.required}
                          onChange={(e) =>
                            updateRule(dimIndex, ruleIndex, { required: e.target.checked })
                          }
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={rule.caseSensitive}
                          onChange={(e) =>
                            updateRule(dimIndex, ruleIndex, { caseSensitive: e.target.checked })
                          }
                        />
                      </td>
                      <td>
                        {dim.rules.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRule(dimIndex, ruleIndex)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--semantic-error)',
                              cursor: 'pointer',
                              fontSize: 16,
                            }}
                          >
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => addRule(dimIndex)}
                style={{ marginTop: 8, fontSize: 12, padding: '4px 12px' }}
              >
                + Add Rule
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-secondary"
          onClick={addDimension}
          style={{ marginBottom: 16 }}
        >
          + Add Dimension
        </button>

        {/* Submit */}
        <div className="card">
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Rubric'}
            </button>
            <Link to="/console/trainer/rubrics" className="btn btn-secondary">
              Cancel
            </Link>
          </div>
          <p className="text-muted text-small" style={{ marginTop: 12 }}>
            Rubrics are created in draft status. Publish them to make them available for assessments.
          </p>
        </div>
      </form>

      <RequestPanel result={result} loading={loading} />
    </div>
  );
}
